/**
 * DialogNuevoCargo — Modal para registrar un cargo a un paciente.
 *
 * Carga el catálogo de servicios y permite:
 *  - Seleccionar de catálogo (con buscador)
 *  - Escribir descripción libre
 *  - Editar cantidad / precio / descuento
 *  - Agregar notas
 */
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Dialog, Slide,
  Autocomplete, FormControl, InputLabel, Select, MenuItem, Chip, CircularProgress, Alert,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const API_URL = '/api';

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2, background: '#f8fafc', fontSize: 13.5,
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: '#1e3a5f', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12.5, fontWeight: 600, color: '#475569' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' },
};

function fmtMoney(num, moneda = 'MXN') {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: moneda }).format(Number(num || 0));
}

export default function DialogNuevoCargo({ open, onClose, paciente, userId, onCreado, consultaId = null }) {
  const [servicios, setServicios] = useState([]);
  const [servicioSel, setServicioSel] = useState(null);
  const [descripcion, setDescripcion] = useState('');
  const [cantidad, setCantidad] = useState('1');
  const [precio, setPrecio] = useState('');
  const [descuento, setDescuento] = useState('');
  const [notas, setNotas] = useState('');
  const [moneda, setMoneda] = useState('MXN');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  // Cargar catálogo al abrir
  useEffect(() => {
    if (!open) return;
    setError('');
    setServicioSel(null);
    setDescripcion('');
    setCantidad('1');
    setPrecio('');
    setDescuento('');
    setNotas('');

    fetch(`${API_URL}/emr/finanzas/servicios?activo=true`, {
      headers: { 'X-User-Id': userId },
    })
      .then(r => r.ok ? r.json() : [])
      .then(setServicios)
      .catch(console.error);
  }, [open, userId]);

  // Cuando selecciona un servicio del catálogo, pre-llena
  const handleSelectServicio = (servicio) => {
    setServicioSel(servicio);
    if (servicio) {
      setDescripcion(servicio.nombre);
      setPrecio(String(servicio.precio));
      setMoneda(servicio.moneda || 'MXN');
    }
  };

  const subtotal = useMemo(() => {
    const c = parseFloat(cantidad) || 0;
    const p = parseFloat(precio) || 0;
    return c * p;
  }, [cantidad, precio]);

  const total = useMemo(() => {
    const d = parseFloat(descuento) || 0;
    return Math.max(0, subtotal - d);
  }, [subtotal, descuento]);

  const valido = descripcion.trim().length >= 2
    && parseFloat(cantidad) > 0
    && parseFloat(precio) >= 0;

  const handleGuardar = async () => {
    if (!valido) return;
    setGuardando(true);
    setError('');
    try {
      const body = {
        paciente_id: paciente.id,
        consulta_id: consultaId,
        servicio_id: servicioSel?.id || null,
        descripcion: descripcion.trim(),
        cantidad: parseFloat(cantidad),
        precio_unitario: parseFloat(precio),
        descuento: parseFloat(descuento || '0'),
        moneda,
        notas: notas.trim() || null,
      };
      const res = await fetch(`${API_URL}/emr/finanzas/cargos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt.substring(0, 250));
      }
      onCreado?.();
    } catch (e) {
      setError(e.message || 'Error al crear cargo');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !guardando && onClose()}
      TransitionComponent={SlideUp}
      maxWidth="xs" fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3, background: 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 24px 60px rgba(15,23,42,0.25)', overflow: 'hidden',
        },
      }}
    >
      {/* Header slim */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.2,
        py: 1, pl: 2, pr: 1,
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)', color: '#fff',
      }}>
        <ReceiptIcon sx={{ fontSize: 18, color: '#fbbf24' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: 0.2, lineHeight: 1.1 }}>
            Nuevo cargo
          </Typography>
          <Typography sx={{ fontSize: 10.5, opacity: 0.75 }}>
            {paciente?.nombre_completo}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={guardando}
          sx={{ color: 'rgba(255,255,255,0.85)', p: 0.4, '&:hover': { background: 'rgba(255,255,255,0.12)' } }}>
          <CloseIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '74vh', overflowY: 'auto' }}>

        {/* Selector de servicio del catálogo */}
        <Autocomplete
          size="small"
          options={servicios}
          getOptionLabel={(o) => o.nombre || ''}
          value={servicioSel}
          onChange={(_, v) => handleSelectServicio(v)}
          sx={inputStyle}
          renderInput={(p) => (
            <TextField {...p} label="Buscar en catálogo (opcional)" placeholder="Consulta general..." />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between !important', gap: 1 }}>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{option.nombre}</Typography>
                <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
                  {option.codigo && `${option.codigo} · `}{option.categoria}
                </Typography>
              </Box>
              <Chip
                label={fmtMoney(option.precio, option.moneda)}
                size="small"
                sx={{
                  height: 22, fontSize: 11.5, fontWeight: 700,
                  background: '#eff6ff', color: '#1e3a5f',
                }}
              />
            </Box>
          )}
        />

        {/* Descripción */}
        <TextField
          sx={inputStyle} size="small"
          label="Descripción del cargo"
          placeholder="Ej: Consulta general"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          required
        />

        {/* Cantidad / precio */}
        <Box sx={{ display: 'flex', gap: 1.2 }}>
          <TextField
            sx={{ ...inputStyle, flex: 0.7 }} size="small"
            type="number" label="Cantidad"
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
          />
          <TextField
            sx={{ ...inputStyle, flex: 1 }} size="small"
            type="number" label="Precio unitario" required
            value={precio}
            onChange={(e) => setPrecio(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
          />
          <FormControl size="small" sx={{ ...inputStyle, minWidth: 90 }}>
            <InputLabel shrink>Moneda</InputLabel>
            <Select label="Moneda" notched value={moneda} onChange={(e) => setMoneda(e.target.value)}>
              <MenuItem value="MXN">MXN</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="COP">COP</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Descuento + notas */}
        <Box sx={{ display: 'flex', gap: 1.2 }}>
          <TextField
            sx={{ ...inputStyle, flex: 0.6 }} size="small"
            type="number" label="Descuento"
            value={descuento}
            onChange={(e) => setDescuento(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
          />
          <TextField
            sx={{ ...inputStyle, flex: 1.4 }} size="small"
            label="Notas (opcional)"
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
          />
        </Box>

        {/* Total */}
        <Box sx={{
          mt: 0.5, p: 1.2, borderRadius: 2,
          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          border: '1px solid #bfdbfe',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box>
            <Typography sx={{ fontSize: 10, color: '#1e40af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Total a cobrar
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#475569', mt: 0.1 }}>
              Subtotal {fmtMoney(subtotal, moneda)}
              {parseFloat(descuento) > 0 && ` − descuento ${fmtMoney(descuento, moneda)}`}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1e3a5f', lineHeight: 1 }}>
            {fmtMoney(total, moneda)}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ py: 0.4, fontSize: 12 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Acciones */}
      <Box sx={{ display: 'flex', gap: 1, p: 2, pt: 0.5, borderTop: '1px solid #f1f5f9' }}>
        <Button
          fullWidth onClick={onClose} disabled={guardando}
          sx={{
            textTransform: 'none', fontWeight: 600, fontSize: 12.5, borderRadius: 2, py: 1,
            color: '#475569', background: '#f1f5f9',
            '&:hover': { background: '#e2e8f0' },
          }}
        >
          Cancelar
        </Button>
        <Button
          fullWidth variant="contained"
          onClick={handleGuardar} disabled={!valido || guardando}
          startIcon={guardando ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
          sx={{
            textTransform: 'none', fontWeight: 700, fontSize: 12.5, borderRadius: 2, py: 1,
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
            boxShadow: '0 4px 12px rgba(30,58,95,0.3)',
            '&:hover': { background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)' },
            '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' },
          }}
        >
          {guardando ? 'Guardando...' : 'Crear cargo'}
        </Button>
      </Box>
    </Dialog>
  );
}
